/**
 * Shared SaaS dialog shell (campaign quick view, Create Design): rounded themed frame capped to the
 * viewport, sticky header (title + extras + close), scrolling body and optional sticky footer.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { LuX } from 'react-icons/lu';
import { TvDiv, TvH5, TvIconButton, TvModalMain, TvModalRoot } from '@trustvid/design-system';

const joinClasses = (...names) => names.filter(Boolean).join(' ');

const SaasDialog = ({
  isOpen,
  onClose,
  title,
  headerExtras = null,
  footer = null,
  children,
  size = 'lg',
  className,
  bodyClassName,
  testId,
  titleTestId,
  bodyTestId,
  footerTestId,
  closeTestId,
  closeLabel = 'Close',
  ...modalProps
}) => (
  <TvModalRoot
    isOpen={isOpen}
    toggle={onClose}
    size={size}
    centered
    className={joinClasses('tv-saas-dialog', className)}
    data-testid={testId}
    {...modalProps}
  >
    <TvDiv className="tv-saas-dialog__head">
      <TvDiv className="tv-saas-dialog__head-title">
        <TvH5
          className="tv-saas-dialog__title mb-0 text-truncate"
          title={typeof title === 'string' ? title : undefined}
          data-testid={titleTestId}
        >
          {title}
        </TvH5>
        {headerExtras}
      </TvDiv>
      <TvIconButton
        type="button"
        color="link"
        className="tv-saas-dialog__close"
        aria-label={closeLabel}
        onClick={onClose}
        data-testid={closeTestId}
      >
        <LuX aria-hidden="true" />
      </TvIconButton>
    </TvDiv>

    <TvModalMain className={joinClasses('tv-saas-dialog__body', bodyClassName)} data-testid={bodyTestId}>
      {children}
    </TvModalMain>

    {footer ? (
      <TvDiv className="tv-saas-dialog__footer" data-testid={footerTestId}>
        {footer}
      </TvDiv>
    ) : null}
  </TvModalRoot>
);

SaasDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  headerExtras: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
  size: PropTypes.string,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  testId: PropTypes.string,
  titleTestId: PropTypes.string,
  bodyTestId: PropTypes.string,
  footerTestId: PropTypes.string,
  closeTestId: PropTypes.string,
  closeLabel: PropTypes.string,
};

export default SaasDialog;
